// Health metrics overview (ADR-023 / requirement 3).
//
// One row per employee showing their LATEST vitals (hr / sbp-dbp / rr) and when
// they were measured — the "誰最近量了、數值如何" at-a-glance board the vendor
// asked for. Same q/source/site filter vocabulary as the employee list so the
// two pages feel like one dataset. Out-of-range values are tinted using the
// same hardcoded clinical bounds the backend evaluator uses (hr 50–120, sbp
// 90–140, dbp 60–90, rr 10–24) so an admin can scan for outliers.

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useI18n, type TFunction } from "@faceheart/i18n";
import { measurementsApi, sitesApi } from "../lib/api/endpoints";
import type { HealthOverviewVitals } from "@faceheart/api-types";

export function HealthOverviewPage() {
  const { t, i18n } = useI18n("admin");
  const [page, setPage] = useState(1);
  const [siteId, setSiteId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => {
      setPage(1);
      setQ(searchInput.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const pageSize = 20;
  const { data, isLoading, error } = useQuery({
    queryKey: ["overview", { page, q, siteId }],
    queryFn: () =>
      measurementsApi.latestOverview({
        page,
        page_size: pageSize,
        q: q || undefined,
        site_id: siteId || undefined,
      }),
  });

  const sitesQ = useQuery({ queryKey: ["sites"], queryFn: sitesApi.list });
  const siteNameById = new Map((sitesQ.data ?? []).map((s) => [s.id, s.name]));
  const dateLocale = i18n.language === "en" ? "en-US" : "zh-TW";

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-xl font-medium">{t("overview.title")}</h1>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("employees.searchPlaceholder")}
            className="bg-admin-bg3 border border-admin-border rounded px-3 py-1.5 text-sm text-admin-text1 placeholder:text-admin-text3 w-56"
          />
          <select
            value={siteId}
            onChange={(e) => {
              setPage(1);
              setSiteId(e.target.value);
            }}
            className="bg-admin-bg3 border border-admin-border rounded px-3 py-1.5 text-sm text-admin-text1"
          >
            <option value="">{t("employees.siteFilterAll")}</option>
            {(sitesQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && <Skeleton />}
      {error && (
        <div className="text-admin-red text-sm">
          {t("employees.loadFailed", { message: (error as Error).message })}
        </div>
      )}

      {data && (
        <>
          <div className="bg-admin-bg2 border border-admin-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-admin-bg3 text-admin-text2 text-xs">
                <tr>
                  <Th>{t("employees.tableHeaders.name")}</Th>
                  <Th>{t("employees.tableHeaders.code")}</Th>
                  <Th>{t("employees.tableHeaders.site")}</Th>
                  <Th>{t("overview.metrics.hr")}</Th>
                  <Th>{t("overview.metrics.bp")}</Th>
                  <Th>{t("overview.metrics.rr")}</Th>
                  <Th>{t("overview.measuredAt")}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-12 text-admin-text3">
                      {t("overview.empty")}
                    </td>
                  </tr>
                ) : (
                  data.items.map((row) => {
                    const v = row.vitals;
                    return (
                      <tr
                        key={row.id}
                        className="border-t border-admin-border hover:bg-admin-bg3/60"
                      >
                        <td className="px-3 py-2.5">
                          <Link
                            to={`/employees/${row.id}`}
                            className="text-admin-cyan hover:underline"
                          >
                            {row.name}
                          </Link>
                        </td>
                        <td className="px-3 py-2.5 text-admin-text2">
                          {row.employee_code}
                        </td>
                        <td className="px-3 py-2.5 text-admin-text2 text-xs">
                          {(row.site_id && siteNameById.get(row.site_id)) || "—"}
                        </td>
                        <Metric value={v.hr} unit="bpm" abnormal={hrAbnormal(v.hr)} />
                        <BpMetric v={v} />
                        <Metric value={v.rr} unit="bpm" abnormal={rrAbnormal(v.rr)} />
                        <td className="px-3 py-2.5 text-admin-text3 text-xs">
                          {fmtDateTime(v.measured_at, dateLocale)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
            t={t}
          />
        </>
      )}
    </div>
  );
}

function Metric({
  value,
  unit,
  abnormal,
}: {
  value: number | null;
  unit: string;
  abnormal: boolean;
}) {
  if (value == null) {
    return <td className="px-3 py-2.5 text-admin-text3">—</td>;
  }
  return (
    <td className={`px-3 py-2.5 ${abnormal ? "text-admin-red" : "text-admin-text1"}`}>
      {value} <span className="text-admin-text3 text-xs">{unit}</span>
    </td>
  );
}

function BpMetric({ v }: { v: HealthOverviewVitals }) {
  if (v.sbp == null && v.dbp == null) {
    return <td className="px-3 py-2.5 text-admin-text3">—</td>;
  }
  const abnormal = sbpAbnormal(v.sbp) || dbpAbnormal(v.dbp);
  return (
    <td className={`px-3 py-2.5 ${abnormal ? "text-admin-red" : "text-admin-text1"}`}>
      {v.sbp ?? "—"}/{v.dbp ?? "—"}{" "}
      <span className="text-admin-text3 text-xs">mmHg</span>
    </td>
  );
}

// Clinical bounds mirror the backend evaluator's hardcoded rules so the tint
// matches what would actually fire an alert.
const hrAbnormal = (x: number | null) => x != null && (x < 50 || x > 120);
const rrAbnormal = (x: number | null) => x != null && (x < 10 || x > 24);
const sbpAbnormal = (x: number | null) => x != null && (x < 90 || x > 140);
const dbpAbnormal = (x: number | null) => x != null && (x < 60 || x > 90);

function Skeleton() {
  return (
    <div className="bg-admin-bg2 border border-admin-border rounded-lg p-4 space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-8 bg-admin-bg3/60 rounded animate-pulse" />
      ))}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-3 py-2 uppercase tracking-wider">{children}</th>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onChange,
  t,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (p: number) => void;
  t: TFunction;
}) {
  const last = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-admin-text2">
      <span>{t("employees.pagination.summary", { total, page, last })}</span>
      <div className="flex gap-2">
        <PageBtn disabled={page <= 1} onClick={() => onChange(page - 1)}>
          {t("employees.pagination.prev")}
        </PageBtn>
        <PageBtn disabled={page >= last} onClick={() => onChange(page + 1)}>
          {t("employees.pagination.next")}
        </PageBtn>
      </div>
    </div>
  );
}

function PageBtn({
  disabled,
  onClick,
  children,
}: {
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="px-3 py-1.5 border border-admin-border rounded text-admin-text2 hover:bg-admin-bg3 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

function fmtDateTime(iso: string | null, locale: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(locale, {
    dateStyle: "short",
    timeStyle: "short",
  });
}
